import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Helper function to get current project ID
const getCurrentProjectId = (): string | null => {
  return localStorage.getItem('selectedProjectId');
};

// Request interceptor to add auth token and project context
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add project ID to requests for transactional APIs
    // Skip project context for org-wide resources (voices, avatars in library)
    const projectId = getCurrentProjectId();
    const url = config.url || '';

    // These endpoints are org-wide and don't need project context
    const isOrgWideEndpoint = 
      url.startsWith('/voices') ||
      url.includes('/voices/') ||
      url.startsWith('/avatars') ||
      url.includes('/avatars/') ||
      url.startsWith('/background-images') ||
      url.includes('/background-images/') ||
      url.startsWith('/projects') ||
      url.includes('/projects/') ||
      url.startsWith('/organizations') ||
      url.includes('/organizations/') ||
      url.startsWith('/users') ||
      url.includes('/users/') ||
      url.startsWith('/roles') ||
      url.includes('/roles/') ||
      url.startsWith('/invitations') ||
      url.includes('/invitations/') ||
      url.startsWith('/integrations') ||
      url.includes('/integrations/') ||
      url.startsWith('/branding') ||
      url.includes('/branding/') ||
      url.startsWith('/publishing') ||
      url.includes('/publishing/') ||
      url.startsWith('/feature-flags') ||
      url.includes('/feature-flags/') ||
      url.startsWith('/billing') ||
      url.includes('/billing/') ||
      url.startsWith('/admin/platform') ||
      url.includes('/admin/platform/') ||
      url.startsWith('/health') ||
      url.includes('/health/') ||
      // Newsroom sources/articles/glossary are org-wide, like voices/avatars —
      // except publish, which deliberately carries the ambient project
      // context through to the TTS job it creates, same as other
      // job-creation endpoints.
      (url.startsWith('/newsroom') && !url.includes('/publish'));

    if (projectId && !isOrgWideEndpoint) {
      // Send project context as header
      config.headers['X-Project-Id'] = projectId;

      // For GET requests, also add as query param for convenience/filters
      if (config.method === 'get') {
        config.params = { ...config.params, projectId };
      }
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Types
export interface Organization {
  _id: string;
  name: string;
  country?: string;
  description?: string;
  industry?: string;
  size?: 'startup' | 'small' | 'medium' | 'large' | 'enterprise';
  website?: string;
  logo?: string;
  settings: {
    timezone: string;
    currency: string;
    dateFormat: string;
    allowPublicProjects: boolean;
    requireApproval: boolean;
    videoTranslation?: {
      pivotThroughEnglish: boolean;
    };
    workflow?: {
      requireApprovedReviewBeforeReassign?: Partial<Record<string, boolean>>;
    };
  };
  subscription: {
    plan: 'free' | 'pro' | 'enterprise';
    status: 'active' | 'inactive' | 'suspended';
    expiresAt?: Date;
  };
  ownerId: string;
  memberCount: number;
  projectCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectMember {
  userId: string;
  role: 'owner' | 'editor' | 'viewer';
  permissions: {
    canEditVoices: boolean;
    canEditTranslations: boolean;
    canEditSpeechToText: boolean;
    canEditTextToSpeech: boolean;
    canManageMembers: boolean;
    canDeleteProject: boolean;
  };
  addedAt: string;
  addedBy: string;
}

export interface ProjectSharing {
  isShared: boolean;
  shareToken?: string;
  shareExpiry?: string;
  allowedDomains: string[];
  requiresApproval: boolean;
  defaultPermission: 'viewer' | 'editor';
}

export interface ProjectStats {
  totalVoices: number;
  totalTranslationJobs: number;
  totalSpeechToTextJobs: number;
  totalTextToSpeechJobs: number;
  lastActivityAt?: string;
}

export interface Project {
  _id: string;
  name: string;
  description?: string;
  status: 'planning' | 'active' | 'on-hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'critical';
  startDate?: string;
  endDate?: string;
  budget?: number;
  organizationId: string;
  ownerId: string;
  
  // Enhanced member management with permissions
  members?: ProjectMember[];
  
  // Simple team members (legacy field)
  teamMembers: string[];
  
  // Project sharing capabilities
  sharing?: ProjectSharing;
  
  // Audio feature associations
  audioFeatures?: {
    voiceIds: string[];
    translationJobIds: string[];
    speechToTextJobIds: string[];
    textToSpeechJobIds: string[];
  };
  
  // Project statistics
  stats?: ProjectStats;
  
  tags: string[];
  settings: {
    isPublic: boolean;
    allowExternalAccess: boolean;
    audioSettings?: {
      defaultVoiceId?: string;
      defaultSourceLanguage: string;
      defaultTargetLanguage: string;
      defaultOutputFormat: 'mp3' | 'wav' | 'ogg';
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface User {
  _id: string;
  name: string;
  email: string;
  role: 'Owner' | 'Admin' | 'Manager' | 'Member' | 'Viewer' | 'User';
  organizationId: string;
  roleId?: string;
  organizations?: Array<{
    organizationId: string;
    roleId: string;
    role: string;
    joinedAt: string;
    isActive: boolean;
  }>;
}

// Organization API
export const organizationApi = {
  // Get current organization
  getCurrent: () => api.get<{ success: boolean; data: Organization }>('/organizations/current'),
  
  // Update organization
  update: (data: Partial<Organization>) => 
    api.put<{ success: boolean; data: Organization; message: string }>('/organizations/current', data),
  
  // Get organization statistics
  getStats: () => api.get<{ success: boolean; data: any }>('/organizations/stats/overview'),
  
  // Get organizations by IDs
  getByIds: (ids: string[]) => 
    api.post<{ success: boolean; data: Organization[] }>('/organizations/by-ids', { ids }),
  
  // Get organization members
  getMembers: (params?: { page?: number; limit?: number; search?: string; role?: string }) =>
    api.get<{ success: boolean; data: { users: User[]; pagination: any } }>('/organizations/members', { params }),
  
  // Add member
  addMember: (data: { email: string; role: string }) =>
    api.post<{ success: boolean; message: string }>('/organizations/members', data),
  
  // Update member role
  updateMember: (userId: string, data: { roleId: string }) =>
    api.put<{ success: boolean; message: string }>(`/organizations/members/${userId}/role`, data),
  
  // Remove member
  removeMember: (userId: string) =>
    api.delete<{ success: boolean; message: string }>(`/organizations/members/${userId}`),

  // Update organization settings (name/country/settings sub-object) — owner only
  updateSettings: (data: { name?: string; country?: string; settings?: Partial<Organization['settings']> }) =>
    api.put<{ success: boolean; data: Organization; message: string }>('/organizations/settings', data),
};

// Project API
export const projectApi = {
  // Get all projects
  getAll: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    priority?: string;
    search?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  }) => api.get<{ success: boolean; data: { projects: Project[]; pagination: any; statistics: any } }>('/projects', { params }),
  
  // Get single project
  getById: (id: string) => api.get<{ success: boolean; data: Project }>(`/projects/${id}`),
  
  // Create project
  create: (data: Partial<Project>) =>
    api.post<{ success: boolean; data: Project; message: string }>('/projects', data),
  
  // Update project
  update: (id: string, data: Partial<Project>) =>
    api.put<{ success: boolean; data: Project; message: string }>(`/projects/${id}`, data),
  
  // Delete project
  delete: (id: string) =>
    api.delete<{ success: boolean; message: string }>(`/projects/${id}`),
  
  // Add team member
  addMember: (id: string, data: { userId: string }) =>
    api.post<{ success: boolean; message: string; data: any }>(`/projects/${id}/members`, data),
  
  // Remove team member
  removeMember: (id: string, userId: string) =>
    api.delete<{ success: boolean; message: string }>(`/projects/${id}/members/${userId}`),
  
  // Get project statistics
  getStats: () => api.get<{ success: boolean; data: any }>('/projects/stats/overview'),
};

// User API
export const userApi = {
  // Get all users in organization
  getAll: (params?: { page?: number; limit?: number; search?: string; role?: string }) =>
    api.get<{ success: boolean; data: { users: User[]; pagination: any } }>('/users', { params }),
  
  // Get current user profile
  getProfile: () => api.get<{ success: boolean; data: User }>('/users/profile'),
  
  // Update profile
  updateProfile: (data: Partial<User>) =>
    api.put<{ success: boolean; data: User; message: string }>('/users/profile', data),
};

export default api;
